import React, { useCallback, useEffect, useState } from 'react';
import { analytics } from '@/services/analytics';
import { clearPinnedBooks, getOfflineSettings, setCellularBuffering } from '@/services/offlineSettings';
import { useOfflineSettings } from '@/hooks/useOfflineSettings';
import { bufferScheduler } from '@/native/bufferScheduler';
import { getStoreUsage, purgeAll, type StoreUsage } from '@/native/assetStore';

/**
 * The user's side of offline listening: how much of the phone it is using,
 * a way to take it back, and whether it may use mobile data.
 *
 * Deliberately not a depth indicator. The buffer is invisible while it works;
 * this card exists because the retention policy never deletes a book in
 * progress on its own, so a clear control is the user's only pressure valve,
 * and because spending someone's mobile data unasked needs an off switch.
 *
 * Native only, and nothing here mentions plans, prices or the website
 * (App Store guideline 3.1.3(a)).
 */

interface OfflineListeningCardProps {
  isDarkMode: boolean;
  cardClasses: string;
  mutedTextClasses: string;
}

const MB = 1024 * 1024;

const formatSize = (bytes: number): string => {
  if (bytes >= 100 * MB) return `${Math.round(bytes / MB)} MB`;
  if (bytes >= MB) return `${(bytes / MB).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
};

const usageLine = (usage: StoreUsage): string => {
  if (usage.bytes === 0) {
    return 'Nothing is stored on this phone right now.';
  }
  return `Using ${formatSize(usage.bytes)} for ${usage.books} book${usage.books === 1 ? '' : 's'}.`;
};

const OfflineListeningCard: React.FC<OfflineListeningCardProps> = ({ isDarkMode, cardClasses, mutedTextClasses }) => {
  const [usage, setUsage] = useState<StoreUsage>(() => getStoreUsage());
  const [confirming, setConfirming] = useState(false);
  const [clearing, setClearing] = useState(false);
  const { cellularBuffering } = useOfflineSettings();

  const refresh = useCallback(() => {
    setUsage(getStoreUsage());
  }, []);

  // The buffer writes to the store while this card is open; every change it
  // reports is a reason to re-read the number.
  useEffect(() => bufferScheduler.subscribe(refresh), [refresh]);
  useEffect(() => {
    refresh();
  }, [refresh]);

  const clear = async () => {
    setClearing(true);
    const before = usage;
    const pinned = getOfflineSettings().pinnedBooks.length;
    try {
      // Pins first, so the scheduler does not start refilling a pinned book
      // between the purge and the re-plan.
      clearPinnedBooks();
      // Not the chapter that is playing: it is being read from its file, and
      // deleting that stops playback a few seconds later.
      await purgeAll({ keepUrls: bufferScheduler.inUseAssetUrls() });
      analytics.track('Offline Downloads Cleared', { bytes: before.bytes, books: before.books, pinned });
      // The open book fills back up as the user listens; that is the feature.
      bufferScheduler.poke();
    } finally {
      setClearing(false);
      setConfirming(false);
      refresh();
    }
  };

  const toggleCellular = () => {
    const next = !cellularBuffering;
    setCellularBuffering(next);
    analytics.track('Offline Mobile Data Changed', { enabled: next });
  };

  const secondaryButton = `px-4 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer border shrink-0 disabled:opacity-50 disabled:cursor-default ${
    isDarkMode ? 'border-zinc-600 text-gray-200 hover:bg-zinc-800' : 'border-gray-300 text-gray-700 hover:bg-gray-100'
  }`;
  const dangerButton = `px-4 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer border shrink-0 disabled:opacity-50 disabled:cursor-default ${
    isDarkMode ? 'border-red-800 text-red-400 hover:bg-red-900/20' : 'border-red-200 text-red-600 hover:bg-red-50'
  }`;

  return (
    <div className={`rounded-xl border shadow-md p-6 sm:p-8 ${cardClasses}`} data-testid="offline-listening-card">
      <h2 className="text-lg font-semibold mb-1">Offline listening</h2>
      <p className={`text-sm ${mutedTextClasses}`}>
        Aleph keeps the next stretch of the book you&apos;re listening to on this phone, so it keeps playing
        through gaps in signal. Nothing is removed until a book is finished or you clear it here.
      </p>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mt-5">
        <p className="text-sm" data-testid="offline-usage">{usageLine(usage)}</p>
        {!confirming && (
          <button
            type="button"
            onClick={() => setConfirming(true)}
            disabled={usage.bytes === 0 || clearing}
            className={secondaryButton}
          >
            Clear downloads
          </button>
        )}
      </div>
      {confirming && (
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mt-3" role="group" aria-label="Confirm clearing downloads">
          <p className={`text-sm ${mutedTextClasses}`}>
            This clears everything stored, downloaded books included. The book you&apos;re listening to fills back up as you listen.
          </p>
          <div className="flex gap-2 shrink-0">
            <button type="button" onClick={() => setConfirming(false)} disabled={clearing} className={secondaryButton}>
              Keep
            </button>
            <button type="button" onClick={() => void clear()} disabled={clearing} className={dangerButton}>
              {clearing ? 'Clearing…' : 'Clear'}
            </button>
          </div>
        </div>
      )}

      <div
        className={`flex flex-col sm:flex-row sm:items-center justify-between gap-4 mt-6 pt-5 border-t ${
          isDarkMode ? 'border-zinc-700' : 'border-gray-200'
        }`}
      >
        <div>
          <h3 className="text-base font-semibold mb-1">Use mobile data</h3>
          <p className={`text-sm ${mutedTextClasses}`}>
            {cellularBuffering
              ? 'The buffer also fills over a mobile connection, about fifteen minutes at a time.'
              : 'Off. The buffer fills only on Wi-Fi; over mobile data the book streams as it always has.'}
          </p>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={cellularBuffering}
          aria-label="Use mobile data"
          onClick={toggleCellular}
          className={`relative inline-flex h-7 w-12 shrink-0 cursor-pointer items-center rounded-full transition-colors ${
            cellularBuffering ? 'bg-blue-700' : isDarkMode ? 'bg-gray-600' : 'bg-gray-300'
          }`}
        >
          <span
            aria-hidden="true"
            className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
              cellularBuffering ? 'translate-x-6' : 'translate-x-1'
            }`}
          />
        </button>
      </div>
    </div>
  );
};

export default OfflineListeningCard;
